import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { TabMenu } from "primereact/tabmenu";
import { useMountEffect } from "primereact/hooks";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import Form from "../../../common/form";
import Section from "../../../common/section";
import FileUpload from "../../../common/fileUpload";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import {
  resetUpload,
  uploadRequest,
} from "../../../../store/slices/uploadSlice";
import {
  createRequest,
  detailsRequest,
  updateDetailRequest,
  associationsRequest,
  setAssociationsQuery,
} from "../../../../store/slices/rewardSlice";
import RewardDetailsForm from "./RewardDetailsForm";
import List from "../../../common/list";
import { TextInput } from "../../../common/fields";

const RewardDetails = ({
  action,
  createRequest,
  detailsRequest,
  error,
  labels,
  record,
  requesting,
  resetUpload,
  updateDetailRequest,
  uploadDetails,
  uploading,
  uploadRequest,
  user,
  progress,
  associationsRequest,
  associationsQuery,
  setAssociationsQuery,
}) => {
  const intl = useIntl();
  const params = useParams();
  const navigate = useNavigate();
  const [bannerFileName, setBannerFileName] = useState("");
  const [isEditable, setIsEditable] = useState(params?.id ? false : true);
  const [activeTab, setActiveTab] = useState(0);
  const [associationSearch, setAssociationSearch] = useState(
    associationsQuery?.entityName || ""
  );

  // Debounce search changes for associations
  useEffect(() => {
    if (activeTab !== 1) return;
    const timer = setTimeout(() => {
      if (associationsQuery?.entityName !== associationSearch) {
        const newQuery = {
          ...associationsQuery,
          pageNo: 1,
          entityName: associationSearch,
        };
        setAssociationsQuery(newQuery);
        associationsRequest({ id: params.id, ...newQuery });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [
    associationSearch,
    activeTab,
    associationsQuery,
    setAssociationsQuery,
    associationsRequest,
    params.id,
  ]);

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.UpdateScope
  );

  useMountEffect(() => {
    resetUpload();
    if (params?.id) {
      detailsRequest({ id: params.id });
    }
  });

  useEffect(() => {
    if ((action === "create" || action === "update") && !requesting && !error) {
      navigate(RouteConstants.APP_ROUTE_REWARDS_LIST);
    }
  }, [action, requesting, error, navigate]);

  const onBannerUpload = (event) => {
    const file = event.files && event.files[0];
    if (file) {
      setBannerFileName(file.name);
      uploadRequest({ file, type: "image" });
    }
  };

  const onSubmit = (values) => {
    let artifactId = null;
    if (uploadDetails?.[bannerFileName]?.artifactId) {
      artifactId = uploadDetails[bannerFileName].artifactId;
    } else if (values.artifactId) {
      artifactId = values.artifactId;
    } else if (record?.artifactId) {
      artifactId = record.artifactId;
    }

    const payload = {
      ...values,
      artifactId,
      totalRewardsAvailable:
        values.totalRewardsAvailable === undefined ||
        values.totalRewardsAvailable === null
          ? 0
          : parseInt(values.totalRewardsAvailable, 10),
    };
    if (record) {
      updateDetailRequest({ id: record.id, payload });
    } else {
      createRequest(payload);
    }
  };

  const getInitialValues = () => {
    if (record) {
      return {
        ...record,
        totalRewardsAvailable: record.totalRewardsAvailable ?? 0,
      };
    }
    return {
      type: 1,
      isActive: true,
      totalRewardsAvailable: 0,
    };
  };

  return (
    <div className="pb-8 md:pb-0">
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={record?.name}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({
              id: params?.id ? "edit_reward" : "add_reward",
            })}
          </div>
          {(params?.id && !isEditable && activeTab === 0 && (
            <div className="edexlms-page-actions">
              <Button
                icon="pi pi-pencil"
                outlined
                onClick={() => setIsEditable(!isEditable)}
                label={intl.formatMessage({ id: "edit" })}
                type="button"
                size="small"
                disabled={!canUpdate}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate ? "" : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          )) ||
            null}
        </div>
        <div
          className="edexlms-page-body"
          style={{
            height: "calc(100vh - 64px - 20px - 45px - 40px - 14px - 33px)",
            overflowY: "scroll",
          }}
        >
          {(params?.id && (
            <TabMenu
              activeIndex={activeTab}
              className="edexlms-tab-menu"
              model={[
                {
                  label: intl.formatMessage({ id: "basic_info" }),
                  command: () => {
                    if (activeTab !== 0) {
                      setActiveTab(0);
                      setIsEditable(false);
                    }
                  },
                },
                {
                  label: intl.formatMessage({ id: "associations" }),
                  command: () => {
                    if (activeTab !== 1) {
                      setActiveTab(1);
                      setIsEditable(false);
                      if (!record?.associations) {
                        associationsRequest({
                          id: params.id,
                          ...associationsQuery,
                        });
                      }
                    }
                  },
                },
              ]}
            />
          )) ||
            null}
          {activeTab === 0 && (
            <Form
              initialValues={getInitialValues()}
              requesting={requesting}
              disabled={!isEditable}
              submitBtn={{
                label: intl.formatMessage({
                  id: params?.id ? "update_reward" : "add_reward",
                }),
                onClick: onSubmit,
                hidden: !isEditable,
              }}
              cancelBtn={{
                label: intl.formatMessage({ id: "cancel" }),
                onClick: () => navigate(RouteConstants.APP_ROUTE_REWARDS_LIST),
                hidden: !isEditable,
              }}
            >
              <div className="flex flex-column gap-4">
                <Section title="reward_details" titleValues={{}}>
                  <RewardDetailsForm
                    disabled={!isEditable}
                    readOnly={!isEditable}
                    uploadDetails={uploadDetails}
                    record={record}
                  />
                </Section>
                <Section title="reward_image" titleValues={{}}>
                  <div className="grid">
                    <div className="col-12">
                      <FileUpload
                        name="banner_file"
                        label="banner_image"
                        infoMessages={[
                          intl.formatMessage({ id: `upload_type_info_image` }),
                          intl.formatMessage(
                            { id: `upload_file_dimensions` },
                            { width: 1350, height: 450 }
                          ),
                        ]}
                        fileType={"image/*"}
                        fileUrl={
                          (record?.artifactUrl &&
                            `${new URL(
                              record?.artifactUrl,
                              process.env.REACT_APP_URL
                            ).toString()}`) ||
                          null
                        }
                        uploading={uploading?.[bannerFileName]}
                        progress={progress?.[bannerFileName] || 0}
                        onUpload={onBannerUpload}
                        readOnly={!isEditable}
                        uploadDetails={uploadDetails?.[bannerFileName]}
                        onClear={() => setBannerFileName("")}
                      />
                    </div>
                  </div>
                </Section>
              </div>
            </Form>
          )}
          {activeTab === 1 && (
            <div className="flex flex-column gap-3">
              <div className="grid">
                <div className="col-12 md:col-4 lg:col-3">
                  <TextInput
                    iconPosition="left"
                    icon="pi-search"
                    placeholder={intl.formatMessage({ id: "search_by_name" })}
                    input={{
                      name: "associationSearch",
                      value: associationSearch,
                      onChange: (e) => setAssociationSearch(e.target.value),
                    }}
                  />
                </div>
              </div>
              <List
                columns={[
                  {
                    field: "entityName",
                    header: "name",
                    sortable: true,
                  },
                  {
                    field: "entityType",
                    header: "entity_type",
                    sortable: true,
                    body: (rowData) => {
                      const entityType = rowData.entityType;
                      if (entityType === "LearningObject")
                        return intl.formatMessage({ id: "learning_object" });
                      if (entityType === "LearningPathway")
                        return intl.formatMessage({ id: "learning_program" });
                      return entityType;
                    },
                  },
                ]}
                data={(record?.associations || []).map((raw) => ({
                  id: `${(
                    raw.entityType ||
                    raw.EntityType ||
                    "misc"
                  ).toLowerCase()}_${
                    raw.entityId || raw.EntityId || raw.id || raw.entityName
                  }`,
                  entityName: raw.entityName || raw.EntityName || "",
                  entityType: raw.entityType || raw.EntityType || "",
                }))}
                loading={
                  (action === "associations" && requesting) ||
                  (record?.associations === undefined && requesting)
                }
                pagination={true}
                selectionMode={null}
                pageQuery={{
                  ...associationsQuery,
                  pageNo: associationsQuery?.pageNo || 1,
                  pageSize: associationsQuery?.pageSize || 20,
                }}
                totalRecords={record?.associationsTotalCount || 0}
                dataKey="id"
                handlePageChange={(pageData) => {
                  const nextPageNo = pageData.page + 1;
                  if (
                    associationsQuery.pageNo !== nextPageNo ||
                    associationsQuery.pageSize !== pageData.rows
                  ) {
                    const newQuery = {
                      ...associationsQuery,
                      pageNo: nextPageNo,
                      pageSize: pageData.rows,
                    };
                    setAssociationsQuery(newQuery);
                    associationsRequest({ id: params.id, ...newQuery });
                  }
                }}
                handleOnSortChange={(sortData) => {
                  const sortColumn = sortData.sortField;
                  const sortBy = sortData.sortOrder === 1 ? "asc" : "desc";
                  if (
                    associationsQuery.sortColumn !== sortColumn ||
                    associationsQuery.sortBy !== sortBy
                  ) {
                    const newQuery = {
                      ...associationsQuery,
                      pageNo: 1,
                      sortColumn,
                      sortBy,
                    };
                    setAssociationsQuery(newQuery);
                    associationsRequest({ id: params.id, ...newQuery });
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  record: state.rewards?.record,
  requesting: state.rewards?.requesting,
  action: state.rewards?.action,
  error: state.rewards?.error,
  user: state.self?.user,
  uploadDetails: state.uploads?.details,
  uploading: state.uploads?.uploading,
  uploadErrors: state.uploads?.error,
  progress: state.uploads?.progress,
  associationsQuery: state.rewards?.associationsQuery,
});

const mapDispatchToProps = (dispatch) => ({
  detailsRequest: (data) => dispatch(detailsRequest(data)),
  createRequest: (data) => dispatch(createRequest(data)),
  updateDetailRequest: (data) => dispatch(updateDetailRequest(data)),
  uploadRequest: (data) => dispatch(uploadRequest(data)),
  resetUpload: () => dispatch(resetUpload()),
  associationsRequest: (data) => dispatch(associationsRequest(data)),
  setAssociationsQuery: (data) => dispatch(setAssociationsQuery(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RewardDetails);
